import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import uuid from 'uuid/v4';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { BackendExperimentContext } from '@atlassian/search-experiment';

import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { RovoConfigProvider } from '@confluence/rovo-config-provider-common/entry-points/RovoConfigProvider.ts';
import { RegisteredExperience } from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { i18n } from './AdvancedSearch-i18n.ts';
import { AdvancedSearchRovoConfigProvider } from './AdvancedSearchRovoConfigProvider.tsx';
import { AdvancedSearchLayout } from './AdvancedSearchLayout.tsx';
import { AnalyticsProvider } from './AnalyticsProvider.tsx';
import { SearchInputProvider } from './SearchInputProvider.tsx';
import { SearchPage } from './SearchPageAsync.tsx';
import { SearchPageLoadUFOExperienceStart } from './SearchPageLoadStart.ts';
import { SearchResultsProvider } from './SearchResultsProvider.tsx';

export const AdvancedSearchController = () => {
	const intl = useIntl();
	const { isRovoEnabled: isRovoEnabledInTCS } = useSessionData();

	// A user sees Full Page Search from `@atlassian/search-page` if:
	// - They have Rovo or the FG for Unified Search beta (in development)
	// For now, the Rovo Opt Out also overrides the Unified Search FG

	const isFullPageSearchEnabled =
		(fg('unified_search_in_confluence') || isRovoEnabledInTCS) &&
		!fg('confluence_rovo_search_opt_out');

	const getFailedSearchJourneyTriggerRovoChatExperiment = useCallback(() => {
		return expVal('failed_search_journey_trigger_rovo_chat', 'cohort', 'control');
	}, []);

	const [shouldShowSearchPageRovoConfig, setShouldShowSearchPageRovoConfig] = useState(false);
	const [shouldSetNudge, setShouldSetNudge] = useState(false);
	const [shouldNudgeToQueryStarters, setShouldNudgeToQueryStarters] = useState(false);
	const [shouldNudgeToSummarizeQuery, setShouldNudgeToSummarizeQuery] = useState(false);

	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const queryParams = getQueryParams();
	const currentQuery = queryParams?.text?.toString() ?? '';
	const searchReferrerId = useRef(queryParams.search_id?.toString()).current;
	const searchSessionId = useRef(uuid()).current;
	const sendSessionStartedEvent = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'advancedSearchScreen',
				action: 'started',
				actionSubject: 'advancedSearchSession',
				attributes: {
					searchSessionId,
					searchReferrerId,
					version: 2,
				},
			},
		});
		analyticsEvent.fire();
	}, [createAnalyticsEvent, searchSessionId, searchReferrerId]);

	const changeSearchIdInQueryParams = useCallback(() => {
		setQueryParams(
			{
				search_id: searchSessionId,
			},
			true,
		);
	}, [setQueryParams, searchSessionId]);

	const handleRedirect = useCallback(() => {
		const { redirect, unsupportedFields, doubleFields } = getQueryParams();
		if (redirect) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'redirected',
					actionSubject: 'advancedSearch',
					source: 'advancedSearchScreen',
					attributes: {
						unsupportedFields,
						doubleFields,
					},
				},
			}).fire();
			setQueryParams({
				redirect: null,
			});
		}
		if (unsupportedFields) {
			setQueryParams({
				unsupportedFields: null,
			});
		}
		if (doubleFields) {
			setQueryParams({
				doubleFields: null,
			});
		}
	}, [createAnalyticsEvent, setQueryParams, getQueryParams]);

	useEffect(() => {
		sendSessionStartedEvent();
		changeSearchIdInQueryParams();
		handleRedirect();
	}, [sendSessionStartedEvent, changeSearchIdInQueryParams, handleRedirect]);

	//Failed Search Journey Trigger Rovo Chat Experiment
	// Control: use the empty AdvancedSearchRovoConfig option
	// Query Starters Only Cohort: use the AdvancedSearchRovoConfig option, do not nudge the user after 20s dwell
	// Nudge to Query Starters Cohort: use the AdvancedSearchRovoConfig option, nudge the user after 20s dwell, nudge redirects user to mini modal
	// Nudge to Summarize Query Cohort: use the AdvancedSearchRovoConfig option, nudge the user after 20s dwell, nudge redirects user to conversation where Rovo will summarize the query results
	useEffect(() => {
		//Only run the experiment if the user is on the full page search page or the query is non-empty
		if (!isFullPageSearchEnabled || currentQuery.length === 0) {
			return;
		}

		//Evaluate the experiment cohort
		const experimentCohort = getFailedSearchJourneyTriggerRovoChatExperiment();
		//If user is in control cohort, do nothing
		if (experimentCohort === 'control') {
			setShouldSetNudge(false);
			return;
		}
		//If user is in query starters only cohort, set the config and return
		if (experimentCohort === 'query-starters-only') {
			setShouldShowSearchPageRovoConfig(true);
			setShouldSetNudge(false);
		}
		//If user is in the nudge to query starters cohort, set the original search page rovo config initially, then set the config with the nudge after 20s dwell
		if (experimentCohort === 'nudge-to-query-starters') {
			setShouldShowSearchPageRovoConfig(true);
			setShouldSetNudge(true);
		}
		//If user is in the nudge to summarize query actioncohort, set the original search page rovo config initially, then set the config with the nudge after 20s dwell
		if (experimentCohort === 'nudge-to-summarize-query') {
			setShouldShowSearchPageRovoConfig(true);
			setShouldSetNudge(true);
		}
		const timeoutId = setTimeout(() => {
			//If a nudge should be set, do so after 20s dwell time
			if (shouldSetNudge) {
				if (experimentCohort === 'nudge-to-query-starters') {
					setShouldNudgeToQueryStarters(true);
				}
				if (experimentCohort === 'nudge-to-summarize-query') {
					setShouldNudgeToSummarizeQuery(true);
				}
				setShouldSetNudge(false);
			}
		}, 20000);
		return () => clearTimeout(timeoutId);
	}, [
		getFailedSearchJourneyTriggerRovoChatExperiment,
		isFullPageSearchEnabled,
		currentQuery,
		createAnalyticsEvent,
		shouldSetNudge,
	]);

	return (
		<UFOSegment name="advanced-search-controller">
			{isFullPageSearchEnabled ? (
				<>
					<RovoConfigProvider
						experience={RegisteredExperience.SEARCH_PAGE}
						provider={AdvancedSearchRovoConfigProvider}
						getSearchQuery={() => getQueryParams()?.text?.toString()}
						shouldUseSearchPageRovoConfig={shouldShowSearchPageRovoConfig}
						shouldNudgeToQueryStarters={shouldNudgeToQueryStarters}
						shouldNudgeToSummarizeQuery={shouldNudgeToSummarizeQuery}
					/>
					<SearchPageLoadUFOExperienceStart />
					<SearchPage />
					<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.header)} />
				</>
			) : (
				<SearchInputProvider>
					<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.header)} />
					<BackendExperimentContext>
						<SearchResultsProvider searchSessionId={searchSessionId}>
							<AnalyticsProvider
								searchSessionId={searchSessionId}
								createAnalyticsEvent={createAnalyticsEvent}
							>
								<AdvancedSearchLayout />
							</AnalyticsProvider>
						</SearchResultsProvider>
					</BackendExperimentContext>
				</SearchInputProvider>
			)}
		</UFOSegment>
	);
};
